import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { PremiumCard } from "./premium-card"
import { PremiumBadge } from "./premium-badge"

const premiumConversationCardVariants = cva(
  "relative overflow-hidden transition-all duration-200 hover:shadow-lg cursor-pointer group",
  {
    variants: {
      status: {
        active: "border-l-4 border-success hover:border-success/60",
        priority: "border-l-4 border-warning hover:border-warning/60",
        escalated: "border-l-4 border-destructive hover:border-destructive/60",
        "ai-processing": "border-l-4 border-primary hover:border-primary/60",
        neutral: "border-l-4 border-neutral-300 hover:border-neutral-400",
      },
      variant: {
        default: "bg-card hover:bg-card/80",
        elevated: "bg-card shadow-premium hover:shadow-lg",
        glass: "glass hover:bg-white/90",
        interactive: "bg-card hover:scale-[1.01] hover:shadow-md hover:border-primary/30",
      }
    },
    defaultVariants: {
      status: "neutral",
      variant: "interactive",
    },
  }
)

export interface PremiumConversationCardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof premiumConversationCardVariants> {
  avatar?: {
    src?: string
    fallback: string
    status?: "online" | "offline" | "away"
  }
  contact: {
    name: string
    email?: string
    company?: string
  }
  lastMessage?: {
    text: string
    timestamp: string
    sender: "user" | "contact" | "ai"
  }
  stats?: {
    messageCount: number
    unreadCount?: number
  }
  tags?: Array<{
    text: string
    variant?: "primary" | "accent" | "success" | "warning" | "destructive"
  }>
  campaign?: {
    id: string
    name: string
  }
  onSelect?: () => void
}

const PremiumConversationCard = React.forwardRef<HTMLDivElement, PremiumConversationCardProps>(
  ({ 
    className, 
    status, 
    variant, 
    avatar, 
    contact, 
    lastMessage, 
    stats, 
    tags, 
    campaign,
    onSelect,
    ...props 
  }, ref) => {
    const getStatusColor = () => {
      switch (status) {
        case "active":
          return "text-success"
        case "priority":
          return "text-warning"
        case "escalated":
          return "text-destructive"
        case "ai-processing":
          return "text-primary"
        default:
          return "text-muted-foreground"
      }
    }

    const getAvatarStatusColor = () => {
      switch (avatar?.status) {
        case "online":
          return "bg-success"
        case "away":
          return "bg-warning"
        default:
          return "bg-neutral-400"
      }
    }

    const getSenderLabel = () => {
      switch (lastMessage?.sender) {
        case "user":
          return "You:"
        case "ai":
          return "AI:"
        default:
          return ""
      }
    }

    return (
      <PremiumCard
        ref={ref}
        variant="elevated"
        padding="default"
        className={cn(premiumConversationCardVariants({ status, variant, className }))}
        onClick={onSelect}
        {...props}
      >
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary/2 via-transparent to-accent/2 opacity-50 group-hover:opacity-70 transition-opacity" />
        
        <div className="relative flex items-start gap-4">
          {/* Avatar */}
          <div className="relative flex-shrink-0">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center text-sm font-semibold text-primary border-2 border-white shadow-sm">
              {avatar?.src ? (
                <img 
                  src={avatar.src} 
                  alt={contact.name} 
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                avatar?.fallback || contact.name.charAt(0).toUpperCase()
              )}
            </div>
            
            {/* Status Indicator */}
            {avatar?.status && (
              <div className={cn(
                "absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white",
                getAvatarStatusColor()
              )} />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                  {contact.name}
                </h3>
                {contact.email && (
                  <p className="text-xs text-muted-foreground truncate">
                    {contact.email}
                  </p>
                )}
                {contact.company && (
                  <p className="text-xs text-muted-foreground/70 truncate">
                    {contact.company}
                  </p>
                )}
              </div>
              
              <div className="flex items-center gap-2 ml-2">
                {stats?.unreadCount && stats.unreadCount > 0 && (
                  <PremiumBadge variant="accent" size="sm">
                    {stats.unreadCount}
                  </PremiumBadge>
                )}
                <PremiumBadge 
                  variant={status === "ai-processing" ? "primary" : "neutral"} 
                  size="sm"
                  dot
                >
                  {status?.replace("-", " ")}
                </PremiumBadge>
              </div>
            </div>

            {/* Last Message */}
            {lastMessage && (
              <div className="mb-3">
                <p className="text-sm text-muted-foreground line-clamp-2">
                  <span className="font-medium text-foreground">
                    {getSenderLabel()}{" "}
                  </span>
                  {lastMessage.text}
                </p>
                <p className="text-xs text-muted-foreground/70 mt-1">
                  {lastMessage.timestamp}
                </p>
              </div>
            )}

            {/* Tags */}
            {tags && tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-3">
                {tags.slice(0, 3).map((tag, index) => (
                  <PremiumBadge 
                    key={index}
                    variant={tag.variant || "outline"} 
                    size="sm"
                  >
                    {tag.text}
                  </PremiumBadge>
                ))}
                {tags.length > 3 && (
                  <PremiumBadge variant="ghost" size="sm">
                    +{tags.length - 3}
                  </PremiumBadge>
                )}
              </div>
            )}

            {/* Campaign Info */}
            {campaign && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <div className="w-1.5 h-1.5 rounded-full bg-primary/60" />
                <span>Campaign: {campaign.name}</span>
              </div>
            )}

            {/* Stats Footer */}
            {stats && (
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50">
                <span className="text-xs text-muted-foreground">
                  {stats.messageCount} messages
                </span>
                <div className={cn("text-xs font-medium", getStatusColor())}>
                  {status?.replace("-", " ")}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Hover Effect */}
        <div className="absolute inset-0 rounded-xl ring-1 ring-inset ring-primary/0 group-hover:ring-primary/20 transition-all duration-200" />
      </PremiumCard>
    )
  }
)
PremiumConversationCard.displayName = "PremiumConversationCard"

export { PremiumConversationCard, premiumConversationCardVariants } 